/**
 * RV Comparison Tool — free side-by-side AI-rated comparison.
 * Path: /tool/rv-compare/:sessionId  (sessionId optional; tool is free)
 */
import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, Sparkles, Trophy, Truck, Link2,
  CheckCircle2, XCircle, Scale, Crown, Wand2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useTheme } from '../context/ThemeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyRv = { year: '', make: '', model: '', notes: '', listing_url: '' };

const RVComparePage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [rv1, setRv1] = useState(emptyRv);
  const [rv2, setRv2] = useState(emptyRv);
  const [priorities, setPriorities] = useState('');
  const [loading, setLoading] = useState(false);
  const [comparison, setComparison] = useState(null);

  const setField = (which, key) => (e) => {
    const val = e.target.value;
    if (which === 1) setRv1((p) => ({ ...p, [key]: val }));
    else setRv2((p) => ({ ...p, [key]: val }));
  };

  const validRV = (rv) => (rv.make && rv.model) || rv.listing_url;

  const handleCompare = async () => {
    if (!validRV(rv1) || !validRV(rv2)) {
      toast.error('Enter make + model OR a listing URL for both RVs');
      return;
    }
    setLoading(true);
    setComparison(null);
    try {
      const res = await axios.post(`${API}/tools/rv-compare`, {
        rv1, rv2, user_priorities: priorities || null,
      });
      setComparison(res.data.comparison);
      // Scroll to result after a tick
      setTimeout(() => {
        document.getElementById('compare-results')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Comparison failed — please try again');
    } finally {
      setLoading(false);
    }
  };

  const headerBg = isDark ? 'bg-gradient-to-br from-slate-900 via-gray-900 to-black' : 'bg-gradient-to-br from-[#004E86] via-[#003d6b] to-[#002d50]';

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-950' : 'bg-amber-50/40'}`} data-testid="rv-compare-page">
      {/* Hero */}
      <div className={`${headerBg} relative overflow-hidden`}>
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[#F2A900]/15 blur-3xl pointer-events-none" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-10 relative">
          <button
            type="button"
            onClick={() => sessionId ? navigate(`/my-matches/${sessionId}`) : navigate('/')}
            className="inline-flex items-center gap-2 text-sm text-blue-100 hover:text-white mb-5 transition-colors"
            data-testid="rv-compare-back"
          >
            <ArrowLeft size={16} /> Back to dashboard
          </button>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-2xl bg-[#F2A900] text-black flex items-center justify-center shadow-xl">
              <Scale size={24} />
            </div>
            <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full bg-emerald-500 text-white">FREE TOOL</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            RV vs RV — AI Comparison Sheet
          </h1>
          <p className="text-blue-100 mt-2 text-sm sm:text-base max-w-2xl">
            Drop in the basics on two RVs (or paste a listing URL) and our AI scores them across 7 categories with a clear winner.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-8 space-y-6">
        {/* Input cards */}
        <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
          <RVCard which={1} rv={rv1} setField={setField} isDark={isDark} accent="blue" />
          <RVCard which={2} rv={rv2} setField={setField} isDark={isDark} accent="amber" />
        </div>

        {/* Priorities + CTA */}
        <div className={`rounded-2xl p-5 ${isDark ? 'bg-gray-900 border border-gray-800' : 'bg-white border border-amber-200'}`}>
          <Label htmlFor="priorities" className={`mb-2 block ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Your buying priorities <span className="text-gray-500 font-normal">(optional, but boosts accuracy)</span>
          </Label>
          <Input
            id="priorities"
            value={priorities}
            onChange={(e) => setPriorities(e.target.value)}
            placeholder="e.g. full-time living with 2 kids, prefer off-grid capability and storage"
            className={isDark ? 'bg-gray-800 border-gray-700 text-white' : ''}
            data-testid="rv-compare-priorities"
          />
          <Button
            onClick={handleCompare}
            disabled={loading}
            className="mt-4 w-full sm:w-auto bg-gradient-to-r from-[#F2A900] to-orange-500 hover:brightness-110 text-black font-extrabold text-base px-7 py-6 shadow-lg"
            data-testid="rv-compare-submit"
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin mr-2" /> Comparing…
              </>
            ) : (
              <>
                <Wand2 size={18} className="mr-2" /> Generate AI comparison
              </>
            )}
          </Button>
        </div>

        {/* Results */}
        {comparison && (
          <ComparisonSheet comparison={comparison} isDark={isDark} />
        )}
      </div>
    </div>
  );
};

const RVCard = ({ which, rv, setField, isDark, accent }) => {
  const accentBg = accent === 'blue'
    ? (isDark ? 'bg-blue-500/10 border-blue-500/40' : 'bg-blue-50 border-blue-300')
    : (isDark ? 'bg-amber-500/10 border-amber-500/40' : 'bg-amber-50 border-amber-300');
  const badgeColor = accent === 'blue' ? 'bg-blue-500' : 'bg-amber-500';
  return (
    <div className={`rounded-2xl border-2 ${accentBg} p-5`} data-testid={`rv-compare-card-${which}`}>
      <div className="flex items-center gap-2 mb-4">
        <span className={`text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${badgeColor} text-white`}>
          RV {which === 1 ? 'A' : 'B'}
        </span>
        <Truck className={isDark ? 'text-gray-400' : 'text-gray-600'} size={16} />
      </div>

      <div className="grid grid-cols-3 gap-2 mb-3">
        <Input placeholder="Year" value={rv.year} onChange={setField(which, 'year')} className={isDark ? 'bg-gray-800 border-gray-700 text-white' : ''} data-testid={`rv${which}-year`} />
        <Input placeholder="Make" value={rv.make} onChange={setField(which, 'make')} className={isDark ? 'bg-gray-800 border-gray-700 text-white' : ''} data-testid={`rv${which}-make`} />
        <Input placeholder="Model" value={rv.model} onChange={setField(which, 'model')} className={isDark ? 'bg-gray-800 border-gray-700 text-white' : ''} data-testid={`rv${which}-model`} />
      </div>

      <div className="relative mb-3">
        <Link2 size={14} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
        <Input
          placeholder="Paste a listing URL (optional)"
          value={rv.listing_url}
          onChange={setField(which, 'listing_url')}
          className={`pl-8 ${isDark ? 'bg-gray-800 border-gray-700 text-white' : ''}`}
          data-testid={`rv${which}-listing`}
        />
      </div>

      <Textarea
        placeholder="Notes — features, price, anything you want AI to weigh"
        value={rv.notes}
        onChange={setField(which, 'notes')}
        rows={3}
        className={`text-sm ${isDark ? 'bg-gray-800 border-gray-700 text-white placeholder:text-gray-500' : ''}`}
        data-testid={`rv${which}-notes`}
      />
    </div>
  );
};

const ComparisonSheet = ({ comparison, isDark }) => {
  const winner = comparison.winner;
  const winnerLabel = winner === 'rv1' ? comparison.rv1_label : winner === 'rv2' ? comparison.rv2_label : 'Tie';

  return (
    <div id="compare-results" className="space-y-5" data-testid="compare-results">
      {/* Winner banner */}
      <div className={`rounded-2xl p-5 sm:p-6 text-center ${isDark ? 'bg-gradient-to-br from-emerald-900/30 to-gray-900 border border-emerald-700/50' : 'bg-gradient-to-br from-emerald-50 to-white border border-emerald-300'}`}>
        <div className="flex items-center justify-center gap-2 mb-2">
          <Trophy className="text-amber-500" size={22} />
          <span className={`text-xs font-bold uppercase tracking-widest ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>AI Verdict</span>
        </div>
        <h2 className={`text-2xl sm:text-3xl font-extrabold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          {winner === 'tie' ? "It's a tie." : `Winner: ${winnerLabel}`}
        </h2>
        <p className={`text-sm sm:text-base max-w-2xl mx-auto ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{comparison.verdict}</p>
        <div className="grid grid-cols-2 gap-3 max-w-md mx-auto mt-4">
          <ScoreBadge label={comparison.rv1_label} score={comparison.rv1_total} highlight={winner === 'rv1'} isDark={isDark} />
          <ScoreBadge label={comparison.rv2_label} score={comparison.rv2_total} highlight={winner === 'rv2'} isDark={isDark} />
        </div>
      </div>

      {/* Category scoresheet */}
      <div className={`rounded-2xl overflow-hidden ${isDark ? 'bg-gray-900 border border-gray-800' : 'bg-white border border-amber-200'}`}>
        <div className={`px-5 py-3 ${isDark ? 'bg-gray-800/40 border-b border-gray-800' : 'bg-amber-50 border-b border-amber-200'}`}>
          <h3 className={`text-sm font-bold uppercase tracking-wider ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>Category Breakdown</h3>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-800">
          {(comparison.categories || []).map((cat, i) => (
            <CategoryRow key={i} cat={cat} rv1Label={comparison.rv1_label} rv2Label={comparison.rv2_label} isDark={isDark} />
          ))}
        </div>
      </div>

      {/* Pros / Cons */}
      <div className="grid md:grid-cols-2 gap-4">
        <ProConCard label={comparison.rv1_label} pros={comparison.rv1_pros || []} cons={comparison.rv1_cons || []} isDark={isDark} accent="blue" testId="rv1-proscons" />
        <ProConCard label={comparison.rv2_label} pros={comparison.rv2_pros || []} cons={comparison.rv2_cons || []} isDark={isDark} accent="amber" testId="rv2-proscons" />
      </div>

      {/* CTA: VIP upgrade */}
      <div className={`rounded-2xl p-5 ${isDark ? 'bg-amber-900/20 border border-amber-700/40' : 'bg-amber-50 border border-amber-300'}`}>
        <div className="flex items-start gap-3">
          <Crown className="text-amber-500 shrink-0 mt-0.5" size={22} />
          <div className="flex-1">
            <p className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Want every RV deal evaluated like this — automatically?</p>
            <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>VIP members get the full RV Deal Analyzer + Negotiation Coach + daily curated matches.</p>
            <Link to="/tools-bundle" className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold text-sm">
              <Sparkles size={14} /> See the bundle
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const ScoreBadge = ({ label, score, highlight, isDark }) => (
  <div className={`rounded-xl p-3 ${
    highlight
      ? 'bg-gradient-to-br from-amber-400 to-orange-500 text-black shadow-lg'
      : isDark ? 'bg-gray-800/60 text-gray-200' : 'bg-white border border-gray-200 text-gray-900'
  }`} data-testid={`score-${highlight ? 'winner' : 'loser'}`}>
    <p className="text-xs font-bold uppercase tracking-wide truncate">{label}</p>
    <p className="text-2xl font-extrabold">{score}<span className="text-sm font-bold opacity-70">/70</span></p>
  </div>
);

const CategoryRow = ({ cat, rv1Label, rv2Label, isDark }) => {
  const r1 = cat.rv1_score || 0;
  const r2 = cat.rv2_score || 0;
  return (
    <div className={`p-4 sm:p-5 ${isDark ? '' : ''}`}>
      <div className="flex items-center justify-between mb-3 gap-3">
        <h4 className={`font-bold text-sm sm:text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>{cat.name}</h4>
        <div className="flex items-center gap-3 text-xs font-bold">
          <span className={r1 >= r2 ? 'text-emerald-500' : (isDark ? 'text-gray-500' : 'text-gray-400')}>{r1}/10</span>
          <span className={isDark ? 'text-gray-700' : 'text-gray-300'}>vs</span>
          <span className={r2 > r1 ? 'text-emerald-500' : (isDark ? 'text-gray-500' : 'text-gray-400')}>{r2}/10</span>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className={`text-xs sm:text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
          <span className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{rv1Label}</span>
          <p className="mt-1">{cat.rv1_note}</p>
        </div>
        <div className={`text-xs sm:text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
          <span className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>{rv2Label}</span>
          <p className="mt-1">{cat.rv2_note}</p>
        </div>
      </div>
    </div>
  );
};

const ProConCard = ({ label, pros, cons, isDark, accent, testId }) => {
  const accentColor = accent === 'blue' ? 'text-blue-500' : 'text-amber-500';
  return (
    <div className={`rounded-2xl p-5 ${isDark ? 'bg-gray-900 border border-gray-800' : 'bg-white border border-amber-200'}`} data-testid={testId}>
      <h4 className={`font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'} ${accentColor}`}>{label}</h4>
      <div className="space-y-2">
        {pros.map((p, i) => (
          <div key={`pro-${i}`} className="flex items-start gap-2 text-sm">
            <CheckCircle2 size={16} className="text-emerald-500 shrink-0 mt-0.5" />
            <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{p}</span>
          </div>
        ))}
        {cons.map((c, i) => (
          <div key={`con-${i}`} className="flex items-start gap-2 text-sm">
            <XCircle size={16} className="text-rose-500 shrink-0 mt-0.5" />
            <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>{c}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RVComparePage;
